import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonBadge from "components/ArgonBadge";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import { useState, useEffect, useRef } from "react";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import { Grid, IconButton, Chip, Tooltip, InputAdornment, Checkbox } from "@mui/material";
import { toast } from "react-toastify";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import { Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import api from "../../../utils/api";
import PaginationBar from "components/PaginationBar/PaginationBar";
import CustomDatePicker from "components/CustomDatePicker/CustomDatePicker";

function DemoManagement() {
  const navigate = useNavigate();
  const [demos, setDemos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const [statusFilter, setStatusFilter] = useState("All");
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const dateParams = {
    from: fromDate?.isValid() ? fromDate.startOf("day").toISOString() : undefined,
    to: toDate?.isValid() ? toDate.endOf("day").toISOString() : undefined,
  };
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  const [bulkStatus, setBulkStatus] = useState("");
  const [bulkReason, setBulkReason] = useState("");
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const bulkUpdateLock = useRef(false);

  const canSelectDemo = (demo) =>
    demo.type === "DEMO" && !["DONE", "COMPLETED"].includes(demo.demoStatus);
  const selectableIds = demos.filter(canSelectDemo).map((demo) => demo._id);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalDemos, setTotalDemos] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setSelectedIds([]);
  }, [currentPage, rowsPerPage, statusFilter, searchInput, fromDate, toDate]);

  // Status counts
  const [statusCounts, setStatusCounts] = useState({
    All: 0,
    SELECTED: 0,
    REQUESTED: 0,
    SCHEDULED: 0,
    COMPLETED: 0,
    CANCELLED: 0,
    REJECTED: 0,
    RESCHEDULED: 0,
  });

  // Fetch demos data with backend pagination
  const fetchDemos = async (signal) => {
    try {
      setLoading(true);

      // Build query parameters
      const params = {
        page: currentPage,
        limit: rowsPerPage,
        search: searchQuery || undefined,
        ...dateParams,
      };

      // Add status filter only if not "All"
      if (statusFilter !== "All") {
        params.status = statusFilter;
      }

      const response = await api.get(`/demo`, { params, signal });
      if (signal.aborted) return;

      console.log("Demo API Response:", response.data);

      if (response.data.success) {
        // Backend returns demos directly in response.data, not in response.data.data
        setDemos(response.data.demos || []);
        const availableIds = (response.data.demos || []).filter(canSelectDemo).map((demo) => demo._id);
        setSelectedIds((previous) => previous.filter((id) => availableIds.includes(id)));
        setTotalDemos(response.data.totalDemos || 0);
        setTotalPages(response.data.totalPages || 0);
      } else {
        console.error("API error:", response.data.message);
        setDemos([]);
        setTotalDemos(0);
        setTotalPages(0);
      }
    } catch (error) {
      if (signal.aborted) return;
      console.error("Error fetching demos:", error);
      setDemos([]);
      setTotalDemos(0);
      setTotalPages(0);
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchQuery(searchInput.trim());
      setCurrentPage(1);
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchInput]);

  useEffect(() => {
    const controller = new AbortController();
    fetchDemos(controller.signal);
    return () => controller.abort();
  }, [refresh, currentPage, rowsPerPage, statusFilter, searchQuery, fromDate, toDate]);

  // Fetch status counts separately
  useEffect(() => {
    const controller = new AbortController();
    fetchStatusCounts(controller.signal);
    return () => controller.abort();
  }, [refresh, searchQuery, fromDate, toDate]);

  const fetchStatusCounts = async (signal) => {
    try {
      const statuses = [
        "SELECTED",
        "REQUESTED",
        "SCHEDULED",
        "DONE",
        "CANCELLED",
        "REJECTED",
        "RESCHEDULED",
      ];
      const counts = { All: 0 };

      // Fetch count for each status
      const promises = statuses.map(async (status) => {
        const response = await api.get(`/demo`, {
          params: { page: 1, limit: 1, status, search: searchQuery || undefined, ...dateParams },
          signal,
        });
        // Backend returns totalDemos directly in response.data, not in response.data.data
        return { status, count: response.data.totalDemos || 0 };
      });

      const results = await Promise.all(promises);
      if (signal.aborted) return;

      results.forEach(({ status, count }) => {
        counts[status] = count;
        counts.All += count;
      });

      setStatusCounts(counts);
    } catch (error) {
      if (signal.aborted) return;
      console.error("Error fetching status counts:", error);
    }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkConfirmOpen || bulkUpdateLock.current || loading || searchInput.trim() !== searchQuery || !bulkStatus || selectedIds.length === 0) return;
    if (bulkStatus === "REJECTED" && !bulkReason.trim()) {
      toast.error("Please enter a rejection reason.");
      return;
    }
    const selectedDemos = demos.filter((demo) => selectedIds.includes(demo._id) && canSelectDemo(demo));
    if (!selectedDemos.length) return;
    bulkUpdateLock.current = true;
    setBulkConfirmOpen(false);
    setBulkUpdating(true);
    setBulkProgress(0);
    const failedIds = [];
    const errors = [];
    let updated = 0;
    let skipped = 0;
    try {
      // Preserve existing CRM updates and other status side effects through the same endpoint.
      for (const [index, demo] of selectedDemos.entries()) {
        if (demo.demoStatus === bulkStatus) {
          skipped += 1;
          setBulkProgress(index + 1);
          continue;
        }
        try {
          const response = await api.put(`/demo/${demo._id}`, {
            demoStatus: bulkStatus,
            ...(bulkStatus === "REJECTED" ? { rejectReason: bulkReason.trim() } : {}),
          });
          if (!response.data.success) throw new Error(response.data.message || "Update failed");
          updated += 1;
        } catch (error) {
          failedIds.push(demo._id);
          const name = demo.students?.[0]?.name || demo.tutorByStudent?.name || demo._id;
          errors.push(`${name}: ${error.response?.data?.message || error.message || "Update failed"}`);
        }
        setBulkProgress(index + 1);
      }
      setSelectedIds(failedIds);
      const resultMessage = `${updated} demo(s) updated.${skipped ? ` ${skipped} already had this status.` : ""}`;
      if (failedIds.length) {
        toast.warning(`${resultMessage} ${failedIds.length} failed. ${errors.join("; ")}`, { autoClose: false });
      } else {
        toast.success(resultMessage);
      }
      if (!failedIds.length) {
        setBulkStatus("");
        setBulkReason("");
      }
    } finally {
      bulkUpdateLock.current = false;
      setBulkUpdating(false);
      setRefresh((previous) => previous + 1);
    }
  };

  // Status badge color mapping
  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "SELECTED":
        return "primary";
      case "REQUESTED":
        return "warning";
      case "SCHEDULED":
        return "info";
      case "DONE":
      case "COMPLETED":
        return "success";
      case "CANCELLED":
        return "error";
      case "REJECTED":
        return "error";
      case "RESCHEDULED":
        return "secondary";
      default:
        return "default";
    }
  };

  // Students List Component with hover functionality
  const StudentsList = ({ students = [], otm = false }) => {
    if (students.length === 0) {
      return (
        <ArgonTypography variant="caption" color="error">
          No Students
        </ArgonTypography>
      );
    }

    const displayStudent = students[0]; // first student
    const extraCount = students.length - 1; // extra students count

    const content = (
      <ArgonBox display="flex" alignItems="center" gap={1}>
        <ArgonBox display="flex" flexDirection="column" alignItems="flex-start">
          <ArgonBox display="flex" alignItems="center" gap={1}>
            <ArgonTypography
              textTransform="none"
              sx={{
                backgroundColor: "#f0f0f0",
                padding: "4px 7px",
                borderRadius: "6px",
              }}
              variant="caption"
              fontWeight="medium"
              color="dark"
            >
              {displayStudent} {extraCount > 0 && `+${extraCount}`}
            </ArgonTypography>
            {otm && (
              <Chip
                label="OTM"
                size="small"
                variant="filled"
                color="success"
                sx={{
                  fontSize: "0.65rem",
                  height: "20px",
                  fontWeight: "medium",
                  borderRadius: "6px",
                  "& .MuiChip-label": {
                    color: "#ffffff", // text white
                  },
                }}
              />
            )}
          </ArgonBox>
        </ArgonBox>
      </ArgonBox>
    );

    const tooltipContent = (
      <ArgonBox sx={{ maxHeight: "200px", overflowY: "auto", padding: "4px" }}>
        <ArgonTypography
          variant="caption"
          fontWeight="bold"
          color="white"
          sx={{ display: "block", marginBottom: "8px" }}
        >
          All Students ({students.length}):
        </ArgonTypography>
        <ArgonBox>
          {students.map((student, index) => (
            <ArgonTypography
              key={index}
              variant="caption"
              color="white"
              sx={{ display: "block", marginBottom: "4px" }}
            >
              • {student}
            </ArgonTypography>
          ))}
        </ArgonBox>
      </ArgonBox>
    );

    return (
      <Tooltip
        title={tooltipContent}
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              backdropFilter: "blur(8px)",
              borderRadius: "8px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
              animation: "fadeInUp 0.3s ease-out",
              "@keyframes fadeInUp": {
                "0%": {
                  opacity: 0,
                  transform: "translateY(10px) scale(0.95)",
                },
                "100%": {
                  opacity: 1,
                  transform: "translateY(0) scale(1)",
                },
              },
            },
          },
          arrow: {
            sx: {
              color: "rgba(0, 0, 0, 0.9)",
            },
          },
        }}
      >
        <ArgonBox sx={{ cursor: "pointer" }}>{content}</ArgonBox>
      </Tooltip>
    );
  };

  StudentsList.propTypes = {
    students: PropTypes.arrayOf(PropTypes.string),
    otm: PropTypes.bool,
  };

  // Prepare table data (no client-side pagination, backend handles it)
  const getTableData = () => {
    const columns = [
      ...(selectionMode ? [{
        name: "selection",
        label: (
          <Checkbox
            size="small"
            checked={allSelected}
            indeterminate={!allSelected && selectedIds.length > 0}
            disabled={bulkUpdating || loading || selectableIds.length === 0}
            onChange={(event) => setSelectedIds(event.target.checked ? selectableIds : [])}
            inputProps={{ "aria-label": "Select all editable demos on this page" }}
          />
        ),
        align: "center",
        width: "3%",
      }] : []),
      { name: "S.No", align: "center", width: "5%" },
      { name: "student", align: "left", width: "20%" },
      { name: "class Name", align: "left", width: "14%" },
      { name: "subject", align: "left", width: "14%" },
      { name: "board", align: "left", width: "14%" },
      { name: "shortlisted tutor", align: "left", width: "23%" },
      { name: "date", align: "center", width: "14%" },
      { name: "status", align: "center", width: "14%" },
      { name: "demo Type", align: "center", width: "12%" },
      { name: "actions", align: "left", width: "12%" },
    ];

    const rows = demos.map((demo, index) => {
      // Extract student names from demo.students array or fallback to tutorByStudent
      const studentNames = [];
      if (Array.isArray(demo.students) && demo.students.length > 0) {
        demo.students.forEach((student) => {
          const name = student?.name || student?.fullName || "Unknown";
          studentNames.push(name);
        });
      } else if (demo.tutorByStudent?.name) {
        studentNames.push(demo.tutorByStudent.name);
      }

      // Extract parent name
      const parentName = demo.parentId?.fullName || demo.parentId?.name || "";
      const parentProfileId = demo.parentId?._id || demo.parentId?.id;
      const tutorProfileId =
        demo.tutorByAdmin?._id || demo.tutorByAdmin?.id || demo.tutorId?._id || demo.tutorId?.id;

      // Calculate serial number based on current page and rows per page
      const serialNumber = (currentPage - 1) * rowsPerPage + index + 1;

      return {
        selection: (
          <Checkbox
            size="small"
            checked={selectedIds.includes(demo._id)}
            disabled={bulkUpdating || !canSelectDemo(demo)}
            onChange={(event) => {
              const checked = event.target.checked;
              setSelectedIds((previous) => checked
                ? [...previous, demo._id]
                : previous.filter((id) => id !== demo._id));
            }}
            inputProps={{ "aria-label": `Select demo for ${studentNames.join(", ") || demo._id}` }}
          />
        ),
        "S.No": (
          <ArgonBox textAlign="center" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {serialNumber}
            </ArgonTypography>
          </ArgonBox>
        ),
        student: (
          <ArgonBox display="flex" flexDirection="column" py={0.5}>
            <ArgonBox display="flex" alignItems="center">
              <StudentsList students={studentNames} otm={demo.OTM} />
            </ArgonBox>
            {parentName && (
              <ArgonTypography
                variant="caption"
                color={parentProfileId ? "primary" : "secondary"}
                component={parentProfileId ? Link : "span"}
                to={parentProfileId ? `/parents/profile/${parentProfileId}` : undefined}
                sx={{
                  mt: 0.5,
                  ml: 0,
                  cursor: parentProfileId ? "pointer" : "default",
                  textDecoration: parentProfileId ? "underline !important" : "none",
                  textUnderlineOffset: parentProfileId ? "2px" : undefined,
                  textDecorationThickness: parentProfileId ? "1px" : undefined,
                  "&:hover": parentProfileId
                    ? {
                        color: "#1976d2",
                        textDecoration: "underline !important",
                      }
                    : {},
                }}
              >
                Client - {parentName}
              </ArgonTypography>
            )}
          </ArgonBox>
        ),
        "class Name": (
          <ArgonBox textAlign="left" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.students?.[0]?.class?.name || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        subject: (
          <ArgonBox textAlign="left" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.students?.[0]?.subjects?.[0]?.name || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        board: (
          <ArgonBox textAlign="left" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.students?.[0]?.board?.name || "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        "shortlisted tutor": (
          <ArgonBox display="flex" alignItems="center" py={0.5}>
            <ArgonBox display="flex" flexDirection="column" minWidth={0} flex={1}>
              <ArgonTypography
                variant="button"
                fontWeight="medium"
                noWrap
                color={tutorProfileId ? "primary" : "text"}
                component={tutorProfileId ? Link : "span"}
                to={tutorProfileId ? `/tutors/profile/${tutorProfileId}` : undefined}
                sx={{
                  cursor: tutorProfileId ? "pointer" : "default",
                  textDecoration: tutorProfileId ? "underline !important" : "none",
                  textUnderlineOffset: tutorProfileId ? "2px" : undefined,
                  textDecorationThickness: tutorProfileId ? "1px" : undefined,
                  "&:hover": tutorProfileId
                    ? {
                        color: "#1976d2",
                        textDecoration: "underline !important",
                      }
                    : {},
                }}
              >
                {demo.tutorByAdmin?.fullName || demo.tutorId?.fullName || "Not Assigned"}
              </ArgonTypography>
              <ArgonTypography variant="caption" color="secondary" noWrap>
                {demo.tutorByAdmin?.email || demo.tutorId?.email || "Admin pending"}
              </ArgonTypography>
            </ArgonBox>
          </ArgonBox>
        ),
        date: (
          <ArgonBox textAlign="center" px={2} py={0.5}>
            <ArgonTypography variant="caption" color="text" fontWeight="medium">
              {demo.demoDate || demo.preferedDate
                ? new Date(demo.demoDate || demo.preferedDate).toLocaleDateString()
                : "N/A"}
            </ArgonTypography>
          </ArgonBox>
        ),
        status: (
          <ArgonBox display="flex" justifyContent="center" px={2} py={0.5}>
            <ArgonBox position="relative" display="inline-flex">
              <ArgonBadge
                variant="gradient"
                badgeContent={
                  demo.demoStatus === "DONE" ? "COMPLETED" : demo.demoStatus || "Unknown"
                }
                color={getStatusColor(demo.demoStatus)}
                size="xs"
                container
              />
              {(demo.demoStatus === "DONE" || demo.demoStatus === "COMPLETED") &&
                demo.demofinalised === false && (
                  <ArgonTypography
                    component="span"
                    fontWeight="bold"
                    sx={{
                      position: "absolute",
                      top: "-12px",
                      right: "-9px",
                      color: "#fb8c00",
                      fontSize: "15px",
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    {"\u20B9"}
                  </ArgonTypography>
                )}
            </ArgonBox>
          </ArgonBox>
        ),
        "demo Type": (
          <ArgonBox display="flex" justifyContent="center" px={2} py={0.5}>
            <Chip
              label={
                demo.demoType
                  ? demo.demoType.charAt(0).toUpperCase() + demo.demoType.slice(1).toLowerCase()
                  : "Online"
              }
              size="small"
              variant="outlined"
              color={
                demo.demoType?.toUpperCase() === "OFFLINE"
                  ? "secondary"
                  : demo.demoType?.toUpperCase() === "BOTH"
                  ? "success"
                  : "primary"
              }
              sx={{ fontSize: "0.75rem", height: "20px" }}
            />
          </ArgonBox>
        ),
        actions: (
          <ArgonBox
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            px={2}
            py={0.5}
          >
            <IconButton
              size="small"
              color="info"
              onClick={() => navigate(`/demos/view/${demo._id}`)}
              disabled={bulkUpdating}
              sx={{
                "&:hover": {
                  backgroundColor: "rgba(25, 118, 210, 0.08)",
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
            {/* Hide Edit button when status is DONE (COMPLETED) */}
            {demo.demoStatus !== "DONE" && demo.demoStatus !== "COMPLETED" && (
              <IconButton
                size="small"
                color="warning"
                onClick={() => navigate(`/demos/edit/${demo._id}`)}
                disabled={bulkUpdating}
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(255, 152, 0, 0.08)",
                  },
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </ArgonBox>
        ),
      };
    });

    return { columns, rows };
  };

  // Status tabs data - Show count from statusCounts state
  const getStatusCounts = () => {
    return [
      { label: "All", count: statusCounts.All, color: "#2027e1ff", status: "All" },

      { label: "Requested", count: statusCounts.REQUESTED, color: "#ff9800", status: "REQUESTED" },

      { label: "Selected", count: statusCounts.SELECTED, color: "#9c27b0", status: "SELECTED" },

      { label: "Scheduled", count: statusCounts.SCHEDULED, color: "#2196f3", status: "SCHEDULED" },

      {
        label: "Rescheduled",
        count: statusCounts.RESCHEDULED,
        color: "#9c27b0",
        status: "RESCHEDULED",
      },

      { label: "Completed", count: statusCounts.DONE, color: "#4caf50", status: "DONE" },

      { label: "Cancelled", count: statusCounts.CANCELLED, color: "#f44336", status: "CANCELLED" },

      { label: "Rejected", count: statusCounts.REJECTED, color: "#f44336", status: "REJECTED" },
    ];
  };

  const { columns, rows } = getTableData();
  const tabsData = getStatusCounts();

  // Handle pagination change
  const handlePageChange = (event, value) => {
    if (bulkUpdateLock.current) return;
    setCurrentPage(value);
  };

  const handleRowsPerPageChange = (event) => {
    if (bulkUpdateLock.current) return;
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1); // Reset to first page
  };

  // Reset to first page when filter changes
  const handleStatusFilterChange = (newStatus) => {
    if (bulkUpdateLock.current) return;
    setStatusFilter(newStatus);
    setCurrentPage(1); // Reset to first page when changing filter
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" gap={2} mb={2} sx={{ overflowX: "auto", pb: 0.5 }}>
                <ArgonTypography variant="h6" fontWeight="medium" sx={{ whiteSpace: "nowrap", flexShrink: 0 }}>
                  Demo Management
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center" gap={1} sx={{ flex: 1, minWidth: "max-content", "& > *": { flexShrink: 0 }, "& > .MuiButton-root": { minWidth: "auto", px: 1.5, whiteSpace: "nowrap" } }}>
                  <CustomDatePicker
                    label="From"
                    value={fromDate}
                    onChange={(value) => {
                      if (value && (!value.isValid() || (toDate && value.isAfter(toDate, "day")))) return;
                      setFromDate(value);
                      setCurrentPage(1);
                    }}
                    width={150}
                    maxDate={toDate || undefined}
                    disabled={bulkUpdating}
                  />
                  <CustomDatePicker
                    label="To"
                    value={toDate}
                    onChange={(value) => {
                      if (value && (!value.isValid() || (fromDate && value.isBefore(fromDate, "day")))) return;
                      setToDate(value);
                      setCurrentPage(1);
                    }}
                    width={150}
                    minDate={fromDate || undefined}
                    disabled={bulkUpdating}
                  />
                  <ArgonButton
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={bulkUpdating || (!fromDate && !toDate)}
                    onClick={() => {
                      setFromDate(null);
                      setToDate(null);
                      setCurrentPage(1);
                    }}
                  >
                    Reset
                  </ArgonButton>
                  <ArgonBox sx={{ flex: "1 0 180px", width: 220 }}>
                    <ArgonInput
                      fullWidth
                      size="small"
                      sx={{ height: 35 }}
                      placeholder="Search Parents, students, tutors or phone numbers"
                      value={searchInput}
                      disabled={bulkUpdating}
                      onChange={(event) => setSearchInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Escape") setSearchInput("");
                      }}
                      inputProps={{ "aria-label": "Search demos by parent, tutor, student or phone", title: "Search by parent, tutor, student or phone" }}
                      startAdornment={
                        <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>
                      }
                      endAdornment={searchInput ? (
                        <InputAdornment position="end">
                          <IconButton size="small" disabled={bulkUpdating} aria-label="Clear demo search" onClick={() => setSearchInput("")}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null}
                    />
                  </ArgonBox>
                  <ArgonButton
                    variant="gradient"
                    color="success"
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={bulkUpdating}
                    onClick={() => navigate("/demos/create")}
                  >
                    Create Demo
                  </ArgonButton>
                  <ArgonButton
                    variant="outlined"
                    color="info"
                    size="small"
                    onClick={() => setRefresh((prev) => prev + 1)}
                    disabled={bulkUpdating}
                  >
                    Refresh
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>

              {/* Status Tabs */}
              <ArgonBox mb={2}>
                <ApnaTabs tabsData={tabsData} activeTab={statusFilter} onTabChange={handleStatusFilterChange} />
              </ArgonBox>
              <ArgonBox display="flex" alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={1}>
                {selectionMode && selectedIds.length > 0 && (
                  <>
                  <ArgonBox
                    role="status"
                    aria-label={`${selectedIds.length} demos selected`}
                    sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, height: 32, px: 1.25, borderRadius: "8px", bgcolor: "#eaf6ff", border: "1px solid #cce7fa", color: "#1673b1", whiteSpace: "nowrap" }}
                  >
                    <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />
                    <ArgonBox component="span" sx={{ fontSize: 12, fontWeight: 600 }}>Selected</ArgonBox>
                    <ArgonBox component="span" sx={{ minWidth: 22, height: 22, px: 0.5, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: "6px", bgcolor: "#1673b1", color: "#fff", fontSize: 12, fontWeight: 700 }}>
                      {selectedIds.length}
                    </ArgonBox>
                  </ArgonBox>
                  <select
                    value={bulkStatus}
                    disabled={bulkUpdating}
                    onChange={(event) => setBulkStatus(event.target.value)}
                    aria-label="Status for selected demos"
                    style={{ width: 150, height: 32, padding: "4px 8px", border: "1px solid #d2d6da", borderRadius: 6, backgroundColor: "#fff", color: "#344767", fontSize: 13, flexShrink: 0 }}
                  >
                    <option value="" disabled>Choose status</option>
                    {[
                      ["REQUESTED", "Requested"], ["SELECTED", "Selected"],
                      ["SCHEDULED", "Scheduled"], ["RESCHEDULED", "Rescheduled"],
                      ["DONE", "Completed"], ["CANCELLED", "Cancelled"], ["REJECTED", "Rejected"],
                    ].map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  {bulkStatus === "REJECTED" && (
                    <ArgonInput
                      placeholder="Rejection reason (required)"
                      value={bulkReason}
                      disabled={bulkUpdating}
                      onChange={(event) => setBulkReason(event.target.value)}
                      inputProps={{ "aria-label": "Rejection reason for selected demos" }}
                      size="small"
                      sx={{ width: 220, maxWidth: "100%", height: 32 }}
                    />
                  )}
                  <ArgonButton
                    size="small"
                    color="info"
                    variant="contained"
                    disabled={bulkUpdating || loading || searchInput.trim() !== searchQuery || !bulkStatus || (bulkStatus === "REJECTED" && !bulkReason.trim())}
                    onClick={() => setBulkConfirmOpen(true)}
                  >
                    {bulkUpdating ? `Updating ${bulkProgress}/${selectedIds.length}...` : "Update Selected"}
                  </ArgonButton>
                  </>
                )}
                <ArgonButton
                  size="small"
                  color="secondary"
                  variant="outlined"
                  disabled={bulkUpdating}
                  aria-pressed={selectionMode}
                  onClick={() => {
                    setSelectionMode((previous) => !previous);
                    setSelectedIds([]);
                    setBulkStatus("");
                    setBulkReason("");
                  }}
                >
                  {selectionMode ? "Cancel Selection" : "Select Demos"}
                </ArgonButton>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
                "& .MuiTableCell-root": {
                  padding: "8px 16px",
                  verticalAlign: "middle",
                },
                "& .MuiTableCell-head": {
                  fontWeight: "600",
                  textTransform: "uppercase",
                  fontSize: "0.75rem",
                  letterSpacing: "0.5px",
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Loading demos...
                  </ArgonTypography>
                </ArgonBox>
              ) : rows && rows.length > 0 ? (
                <>
                  <Table columns={columns} rows={rows} />

                  {/* Pagination Controls */}
                  <PaginationBar
                    currentPage={currentPage}
                    rowsPerPage={rowsPerPage}
                    totalItems={totalDemos}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowsPerPageChange={handleRowsPerPageChange}
                  />
                </>
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="button" color="text">
                    {searchQuery
                      ? "No demos match your search. Try another name or phone number."
                      : statusFilter === "All"
                      ? "No demos available. Create your first demo!"
                      : `No demos match the ${statusFilter} status`}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>
      <DeleteDialog
        open={bulkConfirmOpen}
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={handleBulkStatusUpdate}
        title="Update Demo Status?"
        description={`Change the status of ${selectedIds.length} selected demo(s) to ${bulkStatus === "DONE" ? "Completed" : bulkStatus.charAt(0) + bulkStatus.slice(1).toLowerCase()}?${bulkStatus === "REJECTED" ? ` Reason: ${bulkReason.trim()}` : ""}`}
        warningText="This will apply the chosen status to all selected demos."
        confirmText="Yes, Update Status"
        cancelText="Cancel"
        confirmIcon={<CheckCircleOutlineIcon />}
        confirmColor="info"
      />
      <Footer />
    </DashboardLayout>
  );
}

export default DemoManagement;

